import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { parseItemPrice } from '@/lib/restaurantPublicProfile'
import { appendUserAddress, appendUserPhone } from '@/lib/menuCustomerUser'
import { notifyNewRestaurantOrderTopics } from '@/lib/orderNotifications'
import {
  calculateMenuCouponSplit,
  consumeCouponAfterOrder,
  couponFundedBy,
  couponOrderFields,
  validateCouponForCheckout
} from '@/lib/menuCoupon'

export function formatArabicOrderTime(date = new Date()) {
  const h = date.getHours()
  const hour12 = h % 12 || 12
  const ampm = h >= 12 ? 'م' : 'ص'
  const y = date.getFullYear()
  const m = date.getMonth() + 1
  const d = date.getDate()
  const min = String(date.getMinutes()).padStart(2, '0')
  const sec = String(date.getSeconds()).padStart(2, '0')
  return `${y}/${m}/${d} ${hour12}:${min}:${sec} ${ampm}`
}

/** سطر itemsinorder بالتنسيق المحدث: id:qty:size:extras:price:note */
export function buildItemsInOrderLine({ itemId, qty, sizeLabel = '', extras = '', notes = '', unitPrice }) {
  const id = String(itemId || '').trim()
  const q = Math.max(1, parseInt(qty, 10) || 1)
  const size = String(sizeLabel || '').trim()
  const ex = String(extras || '').trim()
  const note = String(notes || '').trim()
  const price = parseItemPrice(unitPrice)
  const priceStr = price % 1 === 0 ? String(price) : price.toFixed(1)
  return `${id}:${q}:${size}:${ex}:${priceStr}:${note}`
}

/** دمج ملاحظات الأصناف تلقائياً مع ملاحظة التوصيل العامة */
export function buildCombinedOrderNotes(generalNotes, lines) {
  const itemNotesList = (lines || [])
    .filter((l) => l.notes && String(l.notes).trim())
    .map((l) => `• ${l.itemName || l.name || 'صنف'}: ${String(l.notes).trim()}`)

  const g = String(generalNotes || '').trim()
  if (!itemNotesList.length) return g

  const parts = []
  parts.push(`ملاحظات الأصناف:\n${itemNotesList.join('\n')}`)
  if (g) {
    parts.push(`ملاحظات عامة: ${g}`)
  }
  return parts.join('\n\n')
}

/** تحليل سطر itemsinorder بمرونة كاملة لدعم كافة التنسيقات وحقل items_notes */
export function parseItemsInOrderLine(lineStr, order = null, index = -1) {
  const raw = String(lineStr || '').trim()
  const parts = raw.split(':')
  const itemId = parts[0] || ''

  let foundNote = ''
  if (Array.isArray(order?.items_notes)) {
    const matched = order.items_notes.find((n) => String(n.item_id).trim() === itemId)
    if (matched?.note) foundNote = String(matched.note).trim()
  }

  // 1) 6 أجزاء أو أكثر: id:qty:size:extras:price:note
  if (parts.length >= 6) {
    const qty = parseInt(parts[1], 10) || 1
    const size = parts[2] || ''
    const extras = parts[3] || ''
    const price = parseItemPrice(parts[4])
    const note = parts.slice(5).join(':').trim() || foundNote
    return { itemId, qty, size, extras, price, notes: note }
  }

  // 2) 5 أجزاء: id:qty:size:notes:price (النمط الكلاسيكي)
  if (parts.length === 5) {
    const qty = parseInt(parts[1], 10) || 1
    const size = parts[2] || ''
    const note = (parts[3] || '').trim() || foundNote
    const price = parseItemPrice(parts[4])
    return { itemId, qty, size, extras: '', price, notes: note }
  }

  // 3) 4 أجزاء: id:qty:size:price
  if (parts.length === 4) {
    const qty = parseInt(parts[1], 10) || 1
    const size = parts[2] || ''
    const price = parseItemPrice(parts[3])
    return { itemId, qty, size, extras: '', price, notes: foundNote }
  }

  // 4) 3 أجزاء: id:qty:price
  if (parts.length === 3) {
    const qty = parseInt(parts[1], 10) || 1
    const price = parseItemPrice(parts[2])
    return { itemId, qty, size: '', extras: '', price, notes: foundNote }
  }

  return { itemId: raw, qty: 1, size: '', extras: '', price: 0, notes: foundNote }
}

/** كود قصير يقوله العميل للكاشير عند الاستلام */
export function pickupCodeFor(orderid) {
  return `P-${String(orderid).slice(-4)}`
}

export function cartLinesSubtotal(lines) {
  if (!Array.isArray(lines)) return 0
  return lines.reduce((sum, line) => {
    const qty = Math.max(1, parseInt(line?.qty, 10) || 1)
    const price = parseItemPrice(line?.unitPrice)
    return sum + qty * price
  }, 0)
}

/**
 * @param {'app'|'restaurant'} fundedBy restaurant-funded codes are paid from the
 *   food price, so the app fee is taken on items + delivery before the discount.
 */
export function computeMenuOrderFees(itemsSubtotal, deliveryCost, resOrderPerc, discountAmount = 0, fundedBy = 'app') {
  const items = parseItemPrice(itemsSubtotal)
  const delivery = parseItemPrice(deliveryCost)
  const perc = parseItemPrice(resOrderPerc)
  const discount = parseItemPrice(discountAmount)
  const baseForFee = fundedBy === 'restaurant' ? items + delivery : items + delivery - discount
  const serviceFee = perc > 0 && baseForFee > 0 ? baseForFee * (perc / 100) : 0
  const totalprice = items + delivery - discount + serviceFee
  return {
    itemsSubtotal: items,
    deliveryCost: delivery,
    serviceFeePerc: perc,
    serviceFee,
    discountAmount: discount,
    totalprice
  }
}

export function buildRestaurantOrderDocument({
  restaurant,
  cartLines,
  userProfile,
  authUid,
  addressid,
  addressLat,
  addressLong,
  phoneid,
  deliveryCost,
  fees,
  notes = '',
  appliedCoupon = null,
  couponSplit = null,
  deliveryZone = null,
  orderType = 'delivery',
  hallTable = null,
  customerLocation = null
}) {
  const orderid = Date.now()
  const itemsinorder = (cartLines || []).map((line) =>
    buildItemsInOrderLine({
      itemId: line.itemId,
      qty: line.qty,
      sizeLabel: line.sizeLabel,
      extras: line.extras ? (Array.isArray(line.extras) ? line.extras.map(e => e.label).join('+') : String(line.extras)) : '',
      notes: line.notes,
      unitPrice: line.unitPrice
    })
  )

  const items_notes = (cartLines || [])
    .filter((l) => l.notes && String(l.notes).trim())
    .map((l) => ({
      item_id: String(l.itemId || '').trim(),
      item_name: String(l.itemName || l.name || '').trim(),
      note: String(l.notes).trim()
    }))

  const combinedNotes = buildCombinedOrderNotes(notes, cartLines)

  const username = String(userProfile?.username || '').trim()
  const uid = String(authUid || userProfile?.user_id || '').trim()
  const restId = String(restaurant?.id || restaurant?.resturant || '').trim()
  const city = String(restaurant?.where || '').trim()
  const isHall = orderType === 'hall'
  const isPickup = orderType === 'pickup'
  const noDelivery = isHall || isPickup

  return {
    addressid: String(addressid || '').trim(),
    addressLat: String(addressLat ?? '').trim(),
    addressLong: String(addressLong ?? '').trim(),
    userid: uid,
    user_id: uid,
    username,
    phoneid: String(phoneid || '').trim(),
    itemsinorder,
    items_notes,
    resturant: restId,
    where: city,
    deliveryCost: String(fees.deliveryCost),
    serviceFee: String(Number(fees.serviceFee.toFixed(2))),
    serviceFeePerc: String(fees.serviceFeePerc),
    discountAmount: String(fees.discountAmount),
    price: String(fees.itemsSubtotal),
    totalprice: String(Number(fees.totalprice.toFixed(2))),
    orderid,
    status: '1',
    time: formatArabicOrderTime(new Date(orderid)),
    notes: combinedNotes,
    assignedLocation: {
      lat: String(addressLat ?? '').trim(),
      long: String(addressLong ?? '').trim()
    },
    assignedTo: 'false',
    assignedTimestamp: '-1',
    couponcode: appliedCoupon?.code ? String(appliedCoupon.code).trim().toUpperCase() : null,
    ...couponOrderFields(appliedCoupon, couponSplit),
    orderSource: 'web_menu',
    orderType: isHall ? 'hall' : isPickup ? 'pickup' : 'delivery',
    hallTableId: isHall ? String(hallTable?.table_id || '').trim() : '',
    hallTableName: isHall ? String(hallTable?.table_name || '').trim() : '',
    deliveryByOffice: noDelivery ? false : restaurant?.deliveryByOffice !== false,
    deliveryZoneId: noDelivery ? '' : String(deliveryZone?.zone_id || '').trim(),
    deliveryZoneName: noDelivery ? '' : String(deliveryZone?.zone_name || '').trim(),
    // POS: الكاشير بيحصّل طلبات الصالة والاستلام؛ المطبخ بيشوف كل الطلبات
    pickupCode: isPickup ? pickupCodeFor(orderid) : '',
    paymentStatus: 'unpaid',
    kitchenStatus: 'new',
    items_details: (cartLines || []).map((line) => ({
      id: String(line.itemId || ''),
      name: String(line.itemName || line.name || ''),
      section_name: String(line.sectionName || ''),
      size: String(line.sizeLabel || line.size || ''),
      extras: Array.isArray(line.extras) ? line.extras : [],
      quantity: Math.max(1, parseInt(line.qty, 10) || 1),
      price: parseItemPrice(line.unitPrice),
      note: String(line.notes || '')
    })),
    posItems: (cartLines || []).map((line) => ({
      itemId: String(line.itemId || ''),
      name: String(line.itemName || line.name || ''),
      sectionName: String(line.sectionName || ''),
      size: String(line.sizeLabel || line.size || ''),
      extras: [],
      qty: Math.max(1, parseInt(line.qty, 10) || 1),
      unitPrice: parseItemPrice(line.unitPrice),
      notes: String(line.notes || '')
    })),
    createdAtTs: serverTimestamp(),
    posDay: (() => {
      const d = new Date(orderid)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    })(),
    // The customer's own GPS position, when they shared it at checkout (the
    // address fields above fall back to the zone's centre otherwise).
    customerLocation:
      !noDelivery && customerLocation?.lat && customerLocation?.long
        ? {
            lat: String(customerLocation.lat),
            long: String(customerLocation.long),
            accuracy: Number(customerLocation.accuracy) || 0
          }
        : null
  }
}

export async function submitMenuRestaurantOrder({
  restaurant,
  cartLines,
  userProfile,
  userDocId,
  authUid,
  addressid,
  addressLat,
  addressLong,
  phoneid,
  deliveryCost,
  resOrderPerc,
  notes = '',
  savePhone = true,
  saveAddress = true,
  appliedCoupon = null,
  deliveryZone = null,
  orderType = 'delivery',
  hallTable = null,
  customerLocation = null
}) {
  const itemsSubtotal = cartLinesSubtotal(cartLines)
  const isHall = orderType === 'hall'
  const isPickup = orderType === 'pickup'
  const noDelivery = isHall || isPickup
  const effectiveDelivery = noDelivery ? 0 : deliveryCost
  const effectivePerc = noDelivery ? 0 : resOrderPerc
  if (appliedCoupon) {
    const check = validateCouponForCheckout(appliedCoupon, {
      userId: authUid || userProfile?.user_id,
      restaurantId: restaurant?.id || restaurant?.resturant,
      channel: 'menu',
      service: 'restaurant',
      itemTotal: itemsSubtotal,
      orderType: isHall ? 'hall' : isPickup ? 'pickup' : 'delivery'
    })
    if (!check.ok) {
      throw new Error(check.error)
    }
  }

  const couponSplit = calculateMenuCouponSplit({
    appliedCoupon,
    itemTotal: itemsSubtotal,
    deliveryCost: effectiveDelivery
  })
  const fees = computeMenuOrderFees(
    itemsSubtotal,
    effectiveDelivery,
    effectivePerc,
    couponSplit.total,
    couponFundedBy(appliedCoupon)
  )

  const payload = buildRestaurantOrderDocument({
    restaurant,
    cartLines,
    userProfile,
    authUid,
    addressid,
    addressLat,
    addressLong,
    phoneid,
    deliveryCost: effectiveDelivery,
    fees,
    notes,
    appliedCoupon,
    couponSplit,
    deliveryZone: noDelivery ? null : deliveryZone,
    orderType: isHall ? 'hall' : isPickup ? 'pickup' : 'delivery',
    hallTable: isHall ? hallTable : null,
    customerLocation
  })

  const ref = await addDoc(collection(db, 'orders'), payload)

  if (appliedCoupon?.id) {
    try {
      await consumeCouponAfterOrder(appliedCoupon, authUid || userProfile?.user_id)
    } catch (e) {
      console.error('[menuOrder] coupon consume failed', e)
    }
  }

  if (userDocId && savePhone && phoneid) {
    try {
      await appendUserPhone(userDocId, { phoneid })
    } catch {
      /* optional */
    }
  }

  if (userDocId && saveAddress && addressid && !noDelivery) {
    try {
      await appendUserAddress(userDocId, {
        addressid,
        lat: addressLat,
        long: addressLong,
        name: ''
      })
    } catch {
      /* optional */
    }
  }

  try {
    await notifyNewRestaurantOrderTopics({ payload }, {
      ...restaurant,
      id: restaurant?.id || restaurant?.resturant,
      deliveryByOffice: payload.deliveryByOffice,
      orderType: payload.orderType
    })
  } catch {
    /* الطلب نجح — الإشعار اختياري */
  }

  return { id: ref.id, orderid: payload.orderid, payload, fees }
}
